import type { ReactElement, SVGProps } from 'react';

/**
 * Icons are inlined rather than loaded as <img> so they inherit `currentColor`:
 * the same glyph appears white in the header, muted grey when inactive and
 * primary-coloured on interaction. The supplied SVGs hardcode `stroke="white"`,
 * which would freeze all three states.
 *
 * SearchIcon, BellIcon, ShareIcon, FilterIcon and CloseIcon are the supplied
 * artwork, traced from designs/assets. The rest remain placeholders — see
 * ASSETS.md.
 */
type IconProps = SVGProps<SVGSVGElement>;

const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  viewBox: '0 0 24 24',
};

export function SearchIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} viewBox="0 0 25 25" {...props}>
      <path d="M24 24L16.3333 16.3333M1 9.94444C1 11.119 1.23136 12.2821 1.68086 13.3673C2.13036 14.4525 2.7892 15.4386 3.61977 16.2691C4.45034 17.0997 5.43636 17.7585 6.52155 18.208C7.60674 18.6575 8.76984 18.8889 9.94444 18.8889C11.119 18.8889 12.2821 18.6575 13.3673 18.208C14.4525 17.7585 15.4386 17.0997 16.2691 16.2691C17.0997 15.4386 17.7585 14.4525 18.208 13.3673C18.6575 12.2821 18.8889 11.119 18.8889 9.94444C18.8889 8.76984 18.6575 7.60674 18.208 6.52155C17.7585 5.43636 17.0997 4.45034 16.2691 3.61977C15.4386 2.7892 14.4525 2.13036 13.3673 1.68086C12.2821 1.23135 11.119 1 9.94444 1C8.76984 1 7.60674 1.23135 6.52155 1.68086C5.43636 2.13036 4.45034 2.7892 3.61977 3.61977C2.7892 4.45034 2.13036 5.43636 1.68086 6.52155C1.23136 7.60674 1 8.76984 1 9.94444Z" />
    </svg>
  );
}

export function BellIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} viewBox="0 0 22 25" {...props}>
      <path d="M14.7501 18.8889H21.0001C20.3189 18.4946 19.7378 17.9423 19.3036 17.2765C18.8694 16.6107 18.5942 15.85 18.5001 15.0556V11.2222C18.4256 9.60082 17.921 8.03068 17.0402 6.68011C16.1593 5.32953 14.9356 4.24942 13.5001 3.55556C13.5001 2.87778 13.2367 2.22776 12.7679 1.7485C12.299 1.26925 11.6631 1 11.0001 1C10.3371 1 9.70117 1.26925 9.23232 1.7485C8.76348 2.22776 8.50009 2.87778 8.50009 3.55556C7.06458 4.24942 5.84084 5.32953 4.96003 6.68011C4.07922 8.03068 3.57454 9.60082 3.50009 11.2222V15.0556C3.40602 15.85 3.1308 16.6107 2.69658 17.2765C2.26235 17.9423 1.68126 18.4946 1.00009 18.8889H7.25009M14.7501 18.8889H7.25009M14.7501 18.8889V20.1667C14.7501 21.1833 14.355 22.1584 13.6517 22.8772C12.9485 23.5961 11.9947 24 11.0001 24C10.0055 24 9.0517 23.5961 8.34844 22.8772C7.64518 22.1584 7.25009 21.1833 7.25009 20.1667V18.8889" />
    </svg>
  );
}

export function ChevronLeftIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function ChevronDownIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function CloseIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} viewBox="0 0 9 9" {...props}>
      <path d="M8.5 8.5L0.5 0.5M0.5 8.5L8.5 0.5" />
    </svg>
  );
}

export function ShareIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} viewBox="0 0 20 20" {...props}>
      <path d="M6.7 8.7L13.3 5.3M6.7 11.3L13.3 14.7M1 10C1 10.7956 1.31607 11.5587 1.87868 12.1213C2.44129 12.6839 3.20435 13 4 13C4.79565 13 5.55871 12.6839 6.12132 12.1213C6.68393 11.5587 7 10.7956 7 10C7 9.20435 6.68393 8.44129 6.12132 7.87868C5.55871 7.31607 4.79565 7 4 7C3.20435 7 2.44129 7.31607 1.87868 7.87868C1.31607 8.44129 1 9.20435 1 10ZM13 4C13 4.79565 13.3161 5.55871 13.8787 6.12132C14.4413 6.68393 15.2044 7 16 7C16.7956 7 17.5587 6.68393 18.1213 6.12132C18.6839 5.55871 19 4.79565 19 4C19 3.20435 18.6839 2.44129 18.1213 1.87868C17.5587 1.31607 16.7956 1 16 1C15.2044 1 14.4413 1.31607 13.8787 1.87868C13.3161 2.44129 13 3.20435 13 4ZM13 16C13 16.7956 13.3161 17.5587 13.8787 18.1213C14.4413 18.6839 15.2044 19 16 19C16.7956 19 17.5587 18.6839 18.1213 18.1213C18.6839 17.5587 19 16.7956 19 16C19 15.2044 18.6839 14.4413 18.1213 13.8787C17.5587 13.3161 16.7956 13 16 13C15.2044 13 14.4413 13.3161 13.8787 13.8787C13.3161 14.4413 13 15.2044 13 16Z" />
    </svg>
  );
}

export function FilterIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} viewBox="0 0 18 14" {...props}>
      <path d="M1 1H17M3 7H15M6 13H12" />
    </svg>
  );
}

export function PlayIcon(props: IconProps): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M8 5.5v13l11-6.5z" />
    </svg>
  );
}

export function CheckIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <path d="m4 12 5 5L20 6" />
    </svg>
  );
}

export function CheckCircleIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </svg>
  );
}

export function SendIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <path d="M4 12 20 4l-8 16-2-6z" />
    </svg>
  );
}

export function FacebookIcon(props: IconProps): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.9c0-.9.25-1.5 1.55-1.5H16.7V3.6c-.3 0-1.3-.1-2.5-.1-2.5 0-4.2 1.5-4.2 4.3v2.1H7.3V13h2.7v8z" />
    </svg>
  );
}

export function LinkedinIcon(props: IconProps): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M6.94 8.5H4.06V20h2.88zM5.5 3.5a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4M20 13.7c0-3.1-1.66-4.5-3.87-4.5-1.79 0-2.59 1-3.03 1.68V8.5H10.2V20h2.9v-6.2c0-1.4.72-2.2 1.85-2.2s1.9.8 1.9 2.2V20H20z" />
    </svg>
  );
}

export function InstagramIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.2" cy="6.8" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Fallback for the "Zanimljivosti o nama" list until real icons arrive. */
export function FactIcon(props: IconProps): ReactElement {
  return (
    <svg {...base} {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8h.01M11 12h1v4h1" />
    </svg>
  );
}

/**
 * Google's "G" mark, in its brand colours. Google's sign-in branding guidelines
 * require it unaltered on a custom sign-in button, so it takes no fill.
 */
export function GoogleLogo(props: IconProps): ReactElement {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" {...props}>
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/**
 * Facebook's round "f" logo, as its brand guidelines require on a login button.
 * Drawn in the current colour with the "f" cut out, so on Facebook blue it
 * reads as the white logo.
 */
export function FacebookLogo(props: IconProps): ReactElement {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.026 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971H15.83c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z" />
    </svg>
  );
}
